import {
  ArrowRight,
  CloudUpload,
  Link as LinkIcon,
  ListChecks,
  Search,
  ShieldCheck,
  Users,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { Logo } from "../components/app/Logo";
import { Button } from "../components/ui/button";
import { useSession } from "../services/SessionContext";

export function LandingPage() {
  const { repo, signIn } = useSession();
  const navigate = useNavigate();

  function openDemo() {
    const credentials = repo.demoCredentials();
    signIn(credentials.email, credentials.password);
    navigate("/app/dashboard");
  }

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Logo textClassName="text-base sm:text-xl" />
          <div className="flex items-center gap-2">
            <Button asChild className="px-2 sm:px-4" variant="ghost">
              <Link to="/auth/sign-in">Sign in</Link>
            </Button>
            <Button asChild className="hidden sm:inline-flex">
              <Link to="/auth/sign-up">Create Account</Link>
            </Button>
          </div>
        </div>
      </header>

      <main>
        <section className="border-b border-slate-200 bg-white">
          <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 sm:py-16 lg:px-8">
            <div className="max-w-3xl">
              <p className="inline-flex rounded-full bg-navy-50 px-3 py-1 text-sm font-semibold text-navy-900 ring-1 ring-navy-100">
                Baseline event CSV reader
              </p>
              <h1 className="mt-5 text-4xl font-bold leading-tight tracking-normal text-navy-950 sm:text-6xl">
                Upload the log. See what happened.
              </h1>
              <p className="mt-5 max-w-2xl text-lg leading-8 text-slate-700">
                BaselineBoard turns Baseline controller exports into a simple, readable, and
                searchable event history.
              </p>
              <div className="mt-7 grid gap-3 sm:flex">
                <Button asChild size="lg">
                  <Link to="/auth/sign-up">
                    Create Account <ArrowRight className="h-4 w-4" aria-hidden />
                  </Link>
                </Button>
                <Button size="lg" variant="secondary" onClick={openDemo}>
                  View Demo
                </Button>
              </div>
            </div>

            <div className="mt-9 grid gap-3 sm:grid-cols-3">
              {[
                ["Upload CSV", "Use the event export from the controller USB folder.", CloudUpload],
                ["Read timeline", "Plain-English cards keep the raw codes available.", ListChecks],
                ["Share safely", "Create public, client-safe, or support links.", LinkIcon],
              ].map(([title, text, Icon]) => (
                <div
                  key={title as string}
                  className="rounded-lg border border-slate-200 bg-slate-50 p-4"
                >
                  <Icon className="h-5 w-5 text-navy-900" aria-hidden />
                  <h2 className="mt-3 font-semibold text-navy-950">{title as string}</h2>
                  <p className="mt-1 text-sm leading-6 text-slate-600">{text as string}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto grid max-w-6xl gap-8 px-4 py-10 sm:px-6 lg:grid-cols-[0.8fr_1.2fr] lg:px-8">
          <div>
            <h2 className="text-2xl font-bold text-navy-950">Built for one job</h2>
            <p className="mt-3 text-slate-600">
              BaselineBoard turns cryptic controller rows into a reviewable event history without
              fake analytics or extra dashboard noise.
            </p>
          </div>
          <div className="grid gap-3">
            {[
              ["Search by code, zone, program, title, summary, or error text.", Search],
              ["Invite technicians and keep uploads in one company repository.", Users],
              ["Hide user info and serial numbers on client-safe links.", ShieldCheck],
            ].map(([text, Icon]) => (
              <div
                key={text as string}
                className="flex gap-3 rounded-lg border border-slate-200 bg-white p-4"
              >
                <Icon className="mt-0.5 h-5 w-5 flex-none text-navy-900" aria-hidden />
                <p className="text-sm leading-6 text-slate-700">{text as string}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="border-y border-slate-200 bg-slate-50">
          <div className="mx-auto grid max-w-6xl gap-8 px-4 py-10 sm:px-6 lg:grid-cols-3 lg:px-8">
            <div>
              <h2 className="text-lg font-bold text-navy-950">For contractors</h2>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                Upload files from the field and keep the history with the company.
              </p>
            </div>
            <div>
              <h2 className="text-lg font-bold text-navy-950">For support</h2>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                Send a support link with readable events and raw rows when needed.
              </p>
            </div>
            <div>
              <h2 className="text-lg font-bold text-navy-950">For clients</h2>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                Share a clean timeline that hides internal and sensitive details.
              </p>
            </div>
          </div>
        </section>
      </main>

      <footer className="mx-auto max-w-6xl px-4 py-7 text-sm text-slate-600 sm:px-6 lg:px-8">
        <p>© 2026 Sprinkler Nerd. BaselineBoard is a Sprinkler Nerd product.</p>
      </footer>
    </div>
  );
}
